import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSession } from '@clerk/clerk-react';
import { Input, Button, Spinner, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@heroui/react';
import { MagnifyingGlass, Plus, CheckCircle, Buildings } from '@phosphor-icons/react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { toast } from 'react-toastify';
import { ContactCompany } from 'shared/types/contact';

interface SupplierSearchSelectProps {
    value: ContactCompany | null;
    onChange: (supplier: ContactCompany | null) => void;
    linkedSuppliers: ContactCompany[];
    projectPartId?: number;
    onSupplierLinked?: (supplier: ContactCompany) => void;
    placeholder?: string;
    error?: string;
}

interface CreateCompanyForm {
    name: string;
    email: string;
    phone: string;
    vatNumber: string;
}

export const SupplierSearchSelect: React.FC<SupplierSearchSelectProps> = ({
    value,
    onChange,
    linkedSuppliers,
    projectPartId,
    onSupplierLinked,
    placeholder = "Zoek leverancier...",
    error
}) => {
    const { session } = useSession();
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<ContactCompany[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isLinking, setIsLinking] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [createForm, setCreateForm] = useState<CreateCompanyForm>({
        name: '',
        email: '',
        phone: '',
        vatNumber: ''
    });

    const containerRef = useRef<HTMLDivElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Search companies
    const searchCompanies = useCallback(async (query: string) => {
        if (!query || query.length < 2) {
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        try {
            const response = await fetchWithAuth(
                `${API_URL}/contact/search-companies?query=${encodeURIComponent(query)}`,
                { method: 'GET' },
                session
            );

            if (response.ok) {
                const data = await response.json();
                setSearchResults(data);
            }
        } catch (error) {
            console.error('Error searching companies:', error);
        } finally {
            setIsSearching(false);
        }
    }, [session]);

    // Debounced search
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (searchTerm.length >= 2) {
            searchTimeoutRef.current = setTimeout(() => {
                searchCompanies(searchTerm);
            }, 300);
        } else {
            setSearchResults([]);
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm, searchCompanies]);

    // Check if supplier is linked to project part
    const isLinked = useCallback((supplierId: number) => {
        return linkedSuppliers.some(s => s.id === supplierId);
    }, [linkedSuppliers]);

    // Link supplier to project part
    const linkSupplierToPart = useCallback(async (supplier: ContactCompany) => {
        if (!projectPartId) {
            // No project part selected, just select the supplier
            onChange(supplier);
            setShowDropdown(false);
            setSearchTerm('');
            return;
        }

        if (isLinked(supplier.id)) {
            // Already linked, just select
            onChange(supplier);
            setShowDropdown(false);
            setSearchTerm('');
            return;
        }

        setIsLinking(true);
        try {
            const response = await fetchWithAuth(
                `${API_URL}/renovation/project/part/${projectPartId}/supplier/${supplier.id}`,
                { method: 'POST' },
                session
            );

            if (response.ok) {
                toast.success(`${supplier.name} is toegevoegd aan het project onderdeel`);
                onSupplierLinked?.(supplier);
                onChange(supplier);
                setShowDropdown(false);
                setSearchTerm('');
            } else {
                const error = await response.json();
                toast.error(error.message || 'Fout bij toevoegen leverancier');
            }
        } catch (error) {
            console.error('Error linking supplier:', error);
            toast.error('Fout bij toevoegen leverancier aan project onderdeel');
        } finally {
            setIsLinking(false);
        }
    }, [projectPartId, session, onChange, onSupplierLinked, isLinked]);

    // Create new company
    const handleCreateCompany = useCallback(async () => {
        if (!createForm.name.trim()) {
            toast.error('Bedrijfsnaam is verplicht');
            return;
        }

        setIsCreating(true);
        try {
            const response = await fetchWithAuth(
                `${API_URL}/contact/create-company`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        name: createForm.name.trim(),
                        email: createForm.email.trim() || undefined,
                        phone_direct: createForm.phone.trim() || undefined,
                        vatNumber: createForm.vatNumber.trim() || undefined
                    })
                },
                session
            );

            if (response.ok) {
                const newCompany = await response.json();
                toast.success(`${newCompany.name} is aangemaakt`);

                // If we have a project part, link the new supplier
                if (projectPartId) {
                    await linkSupplierToPart(newCompany);
                } else {
                    onChange(newCompany);
                }

                setShowCreateModal(false);
                setCreateForm({ name: '', email: '', phone: '', vatNumber: '' });
                setShowDropdown(false);
                setSearchTerm('');
            } else {
                const error = await response.json();
                toast.error(error.message || 'Fout bij aanmaken bedrijf');
            }
        } catch (error) {
            console.error('Error creating company:', error);
            toast.error('Fout bij aanmaken bedrijf');
        } finally {
            setIsCreating(false);
        }
    }, [createForm, session, projectPartId, linkSupplierToPart, onChange]);

    // Open create modal with search term as initial name
    const handleOpenCreateModal = useCallback(() => {
        setCreateForm(prev => ({ ...prev, name: searchTerm }));
        setShowCreateModal(true);
    }, [searchTerm]);

    return (
        <div ref={containerRef} className="relative">
            {/* Selected value display or search input */}
            {value ? (
                <div className="flex items-center gap-2 p-2 bg-white border border-neutral-primary/20 rounded-lg">
                    <Buildings className="h-5 w-5 text-moss-primary" />
                    <span className="flex-1 text-sm font-medium">{value.name}</span>
                    {isLinked(value.id) && (
                        <CheckCircle className="h-4 w-4 text-moss-primary" weight="fill" />
                    )}
                    <Button
                        size="sm"
                        variant="light"
                        onPress={() => {
                            onChange(null);
                            setSearchTerm('');
                        }}
                        className="min-w-0 px-2"
                    >
                        Wijzig
                    </Button>
                </div>
            ) : (
                <div className="relative">
                    <Input
                        placeholder={placeholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onFocus={() => setShowDropdown(true)}
                        startContent={<MagnifyingGlass className="h-4 w-4 text-neutral-400" />}
                        endContent={isSearching && <Spinner size="sm" />}
                        classNames={{
                            base: "w-full",
                            inputWrapper: "bg-white h-10"
                        }}
                    />
                </div>
            )}

            {/* Dropdown with search results */}
            {showDropdown && !value && (
                <div className="absolute left-0 right-0 top-full mt-1 z-50 max-h-64 overflow-auto rounded-lg border border-neutral-primary/20 bg-white shadow-lg">
                    {/* Linked suppliers section */}
                    {linkedSuppliers.length > 0 && searchTerm.length < 2 && (
                        <>
                            <div className="px-3 py-2 text-xs font-semibold text-neutral-500 bg-neutral-50 border-b">
                                Gekoppelde leveranciers
                            </div>
                            {linkedSuppliers.map((supplier) => (
                                <div
                                    key={supplier.id}
                                    onClick={() => {
                                        onChange(supplier);
                                        setShowDropdown(false);
                                        setSearchTerm('');
                                    }}
                                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-moss-primary/10 border-b border-neutral-100"
                                >
                                    <Buildings className="h-4 w-4 text-moss-primary" />
                                    <span className="flex-1 text-sm">{supplier.name}</span>
                                    <CheckCircle className="h-4 w-4 text-moss-primary" weight="fill" />
                                </div>
                            ))}
                        </>
                    )}

                    {/* Search results */}
                    {searchTerm.length >= 2 && (
                        <>
                            {searchResults.length > 0 && (
                                <div className="px-3 py-2 text-xs font-semibold text-neutral-500 bg-neutral-50 border-b">
                                    Zoekresultaten
                                </div>
                            )}
                            {searchResults.map((supplier) => (
                                <div
                                    key={supplier.id}
                                    onClick={() => !isLinking && linkSupplierToPart(supplier)}
                                    className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-moss-primary/10 border-b border-neutral-100 ${isLinking ? 'opacity-50' : ''}`}
                                >
                                    <Buildings className="h-4 w-4 text-neutral-400" />
                                    <div className="flex-1">
                                        <span className="text-sm">{supplier.name}</span>
                                        {supplier.vatNumber && (
                                            <span className="text-xs text-neutral-400 ml-2">
                                                BTW: {supplier.vatNumber}
                                            </span>
                                        )}
                                    </div>
                                    {isLinked(supplier.id) ? (
                                        <CheckCircle className="h-4 w-4 text-moss-primary" weight="fill" />
                                    ) : (
                                        <span className="text-xs text-moss-primary">+ Koppelen</span>
                                    )}
                                </div>
                            ))}

                            {/* No results message */}
                            {searchResults.length === 0 && !isSearching && (
                                <div className="px-3 py-4 text-center text-sm text-neutral-500">
                                    Geen resultaten gevonden voor "{searchTerm}"
                                </div>
                            )}
                        </>
                    )}

                    {/* Create new company button */}
                    <div
                        onClick={handleOpenCreateModal}
                        className="flex items-center gap-2 px-3 py-3 cursor-pointer hover:bg-moss-primary/10 bg-neutral-50 border-t"
                    >
                        <Plus className="h-4 w-4 text-moss-primary" />
                        <span className="text-sm font-medium text-moss-primary">
                            Nieuw bedrijf aanmaken{searchTerm && `: "${searchTerm}"`}
                        </span>
                    </div>
                </div>
            )}

            {/* Error message */}
            {error && (
                <p className="text-brick-primary text-sm mt-1">{error}</p>
            )}

            {/* Create Company Modal */}
            <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} size="md">
                <ModalContent>
                    <ModalHeader>Nieuw bedrijf aanmaken</ModalHeader>
                    <ModalBody className="gap-4">
                        <Input
                            label="Bedrijfsnaam *"
                            placeholder="Voer bedrijfsnaam in..."
                            value={createForm.name}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                            classNames={{ inputWrapper: "bg-white" }}
                        />
                        <Input
                            label="E-mail"
                            type="email"
                            placeholder="info@bedrijf.be"
                            value={createForm.email}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                            classNames={{ inputWrapper: "bg-white" }}
                        />
                        <Input
                            label="Telefoon"
                            placeholder="+32 ..."
                            value={createForm.phone}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, phone: e.target.value }))}
                            classNames={{ inputWrapper: "bg-white" }}
                        />
                        <Input
                            label="BTW nummer"
                            placeholder="BE0123456789"
                            value={createForm.vatNumber}
                            onChange={(e) => setCreateForm(prev => ({ ...prev, vatNumber: e.target.value }))}
                            classNames={{ inputWrapper: "bg-white" }}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="light"
                            onPress={() => setShowCreateModal(false)}
                        >
                            Annuleren
                        </Button>
                        <Button
                            color="primary"
                            onPress={handleCreateCompany}
                            isLoading={isCreating}
                            className="bg-moss-primary"
                        >
                            Aanmaken
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    );
};
